import { useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { dateTimeNoSeconds } from '../../../shared/dateTime';
import { useFeature } from '../../featureFlags/FeatureGate';
import { useHasPermission } from '../../PermissionGate';
import { showVehicleSuggestions } from '../vehicleSuggestionsAnchor';
import {
  FEATURE_ORDER_CHECKLIST,
  fetchOrderTodo,
  setChecklistItem,
  todoErrorMessage,
  type Checklist,
  type ChecklistItem,
  type OrderTodo,
  type TodoTarget,
  type TodoTone,
} from './orderTodoApi';

/**
 * Panel „Do zrobienia” w oknie zlecenia (prace 24.10.2026, G1/K1; makieta FTL
 * p. Mariusza — panel zlecenia; przełącznik `orderChecklist`, S-24.10-01).
 *
 * Pozycje liczy API (`order-checklist.ts`) z danych, które już mamy. Panel
 * niczego nie zmienia poza czynnościami po obsadzeniu — prowadzi do sekcji
 * karty, gdzie się działa. Makieta stawia go z prawej, obok sekcji
 * roboczych; u nas stoi nad sekcjami, na całą szerokość, pozycje obok
 * siebie — jak na karcie pojazdu (tabele rekomendacji i wariantów mieszczą
 * się przy 1366 px tylko na pełnej szerokości).
 *
 * Czynności po obsadzeniu: zaznacza planowanie FTL (gość i trader oglądają).
 * Zmiana auta albo odpięcie zaczyna listę od nowa — API wie, do którego
 * obsadzenia należy lista.
 */

const TONE_COLOR: Record<TodoTone, (theme: Theme) => string> = {
  error: (t) => t.palette.error.main,
  warning: (t) => t.palette.warning.main,
  info: (t) => t.palette.info.main,
  ok: (t) => t.palette.success.main,
};

/** Sekcje karty, do których prowadzą pozycje (`data-testid` sekcji). */
const TARGET_TESTID: Record<Exclude<TodoTarget, 'suggestions'>, string> = {
  variants: 'ftl-zlecenie-obsadzenie',
  alerts: 'order-window',
  notes: 'ftl-notes-section',
};

function goTo(target: TodoTarget): void {
  if (target === 'suggestions') {
    showVehicleSuggestions();
    return;
  }
  document
    .querySelector(`[data-testid="${TARGET_TESTID[target]}"]`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Kto i kiedy ostatnio zmienił czynność. */
export function checklistStamp(item: ChecklistItem): string | null {
  if (!item.changedAt) return null;
  const who = item.changedBy ?? '—';
  return `${item.done ? 'wykonane' : 'cofnięte'}: ${who}, ${dateTimeNoSeconds(item.changedAt)}`;
}

/** Wiersz „Alokacja”: numer pojazdu, kierowca, nr terminala Trimble. */
export function allocationText(a: Checklist['allocation']): string {
  return [
    `pojazd ${a.vehicle ?? '—'}`,
    `kierowca ${a.driver ?? '—'}`,
    `nr terminala Trimble ${a.trimbleTerminal ?? '—'}`,
  ].join(' · ');
}

const ChecklistSection = ({
  checklist,
  busy,
  onToggle,
}: {
  checklist: Checklist;
  busy: boolean;
  onToggle: (item: ChecklistItem, done: boolean) => void;
}) => (
  <Box sx={{ mt: 1.5 }} data-testid="order-checklist">
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      Czynności po obsadzeniu
    </Typography>
    <Box
      component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: 'none',
        display: 'grid',
        gap: 1,
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
      }}
    >
      {checklist.items.map((item) => (
        <Box
          component="li"
          key={item.key}
          data-testid={`order-checklist-item-${item.key}`}
          sx={[
            { px: 1.5, py: 0.5, bgcolor: 'action.hover', borderRadius: 1, minWidth: 0 },
            (theme: Theme) => ({
              borderLeft: `4px solid ${item.done ? theme.palette.success.main : theme.palette.warning.main}`,
            }),
          ]}
        >
          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              checklist.canCheck ? (
                <Checkbox
                  size="small"
                  checked={item.done}
                  disabled={busy}
                  onChange={(e) => onToggle(item, e.target.checked)}
                  sx={{ pt: 0.25 }}
                  slotProps={{
                    input: { 'aria-label': item.label, ...{ 'data-testid': `order-checklist-check-${item.key}` } },
                  }}
                />
              ) : (
                // Podgląd (trader, gość): stan bez pola wyboru — karta zostaje bez pól edycji.
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', p: 0.75, pt: 0.5 }}
                  role="img"
                  aria-label={`${item.label}: ${item.done ? 'wykonane' : 'do zrobienia'}`}
                  data-testid={`order-checklist-state-${item.key}`}
                  data-done={item.done ? 'true' : 'false'}
                >
                  {item.done ? (
                    <CheckCircleIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                  )}
                </Box>
              )
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {item.hint}
                </Typography>
                {item.key === 'allocation' ? (
                  <Typography variant="caption" sx={{ display: 'block' }} data-testid="order-checklist-allocation">
                    {allocationText(checklist.allocation)}
                  </Typography>
                ) : null}
                {checklistStamp(item) ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {checklistStamp(item)}
                  </Typography>
                ) : null}
              </Box>
            }
          />
        </Box>
      ))}
    </Box>
    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
      {checklist.canCheck
        ? 'Zaznaczenie zapisuje, kto i kiedy, także w historii zlecenia. Nic nie wychodzi na zewnątrz: awizację robi CS, instrukcje przekazuje Odys.'
        : 'Czynności zaznacza planowanie FTL. Nic nie wychodzi na zewnątrz.'}
    </Typography>
  </Box>
);

/** Panel za przełącznikiem — wyłączony: karta bez panelu, jak dotąd. */
export const OrderTodoPanel = () => (useFeature(FEATURE_ORDER_CHECKLIST) ? <OrderTodoCard /> : null);

const OrderTodoCard = () => {
  const r = useRecordContext<RaRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const canSeeSuggestions = useHasPermission('FTL_PLANNING');
  const [todo, setTodo] = useState<OrderTodo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = r?.id as number | string | undefined;
  // Szczegóły zlecenia zmieniają się po każdej operacji — wtedy pytamy od nowa.
  const newest = ((r?.changes as RaRecord[] | undefined) ?? [])[0]?.id ?? null;
  const stamp = `${String(r?.planningStatus ?? '')}|${String(r?.assignedCarId ?? '')}|${String(newest)}`;

  // Inne zlecenie (nawigacja między kartami bez przeładowania) — bez pozycji poprzedniego.
  useEffect(() => {
    setTodo(null);
    setError(null);
  }, [id]);

  useEffect(() => {
    if (id === undefined || id === null) return undefined;
    let alive = true;
    fetchOrderTodo(id)
      .then((t) => {
        if (alive) {
          setTodo(t);
          setError(null);
        }
      })
      .catch(() => {
        if (alive) setError('Nie udało się wczytać panelu „Do zrobienia” — odśwież kartę.');
      });
    return () => {
      alive = false;
    };
  }, [id, stamp]);

  if (!r) return null;

  const toggle = async (item: ChecklistItem, done: boolean) => {
    setBusy(true);
    try {
      setTodo(await setChecklistItem(r.id, item.key, done));
      notify(done ? `„${item.label}” — wykonane` : `„${item.label}” — do zrobienia`, { type: 'info' });
      // Historia zmian dostaje wpis — karta pobiera szczegóły od nowa.
      refresh();
    } catch (e) {
      notify(todoErrorMessage(e), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="order-todo">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Do zrobienia
        </Typography>
        {error && !todo ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}
        {!todo && !error ? (
          <Typography variant="body2" color="text.secondary">
            Wczytuję…
          </Typography>
        ) : null}
        {todo ? (
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
            }}
          >
            {todo.items.map((item) => (
              <Box
                component="li"
                key={item.key}
                data-testid="order-todo-item"
                data-tone={item.tone}
                data-todo-key={item.key}
                sx={[
                  { px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1, minWidth: 0 },
                  (theme: Theme) => ({ borderLeft: `4px solid ${TONE_COLOR[item.tone](theme)}` }),
                ]}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {item.detail}
                </Typography>
                {item.link && (item.link.target !== 'suggestions' || canSeeSuggestions) ? (
                  <Button
                    size="small"
                    endIcon={<ArrowDownwardRoundedIcon />}
                    onClick={() => goTo(item.link!.target)}
                    sx={{ mt: 0.5, px: 0.5 }}
                    data-testid={`order-todo-link-${item.key}`}
                  >
                    {item.link.label}
                  </Button>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : null}
        {todo?.checklist ? <ChecklistSection checklist={todo.checklist} busy={busy} onToggle={toggle} /> : null}
      </CardContent>
    </Card>
  );
};
